/**
 * @fileoverview Query keys for use with react-query, to standardize the keys used across the application,
 *  and enable type-safe query key introspection such as in invalidation logic.
 *
 * Each Key is a tuple. The first element is a prefix, and the rest of the elements are the type-safe key arguments.
 *
 * Standardized keys enables benefits for request sharing, caching, and invalidation.
 */

import type { ProxyTypeEnum, HoldingsSubject, TaxStatusEnum } from 'venn-api';
import type { ApiSubject, StudioAnalysisRequest } from 'venn-state';
import type AnalysisSubject from '../analysis/AnalysisSubject';

export const STUDIO_ANALYSIS_KEY_PREFIX = 'studioAnalysis';
export type StudioAnalysisKey = ReturnType<typeof createStudioAnalysisKey>;
export const createStudioAnalysisKey = (...args: [analysisId: number, requests: StudioAnalysisRequest[]]) =>
  [STUDIO_ANALYSIS_KEY_PREFIX, ...args] as const;

export const FUND_RETURNS_RANGE_KEY_PREFIX = 'fundReturnsRange';
export type FundReturnsRangeKey = ReturnType<typeof createFundReturnsRangeKey>;
export const createFundReturnsRangeKey = (...args: [fundId: string]) =>
  [FUND_RETURNS_RANGE_KEY_PREFIX, ...args] as const;

export const GET_FUND_KEY_PREFIX = 'getFund';
export type GetFundKey = ReturnType<typeof createGetFundKey>;
export const createGetFundKey = (...args: [fundId: string]) => [GET_FUND_KEY_PREFIX, ...args] as const;

export const GET_PORTFOLIO_KEY_PREFIX = 'getPortfolio';
export type GetPortfolioKey = ReturnType<typeof createGetPortfolioKey>;
export const createGetPortfolioKey = (...args: [portfolioId: number]) => [GET_FUND_KEY_PREFIX, ...args] as const;

export const BULK_GET_FUND_KEY_PREFIX = 'getFundBulk';
export type BulkGetFundKey = ReturnType<typeof createBulkGetFundKey>;
export const createBulkGetFundKey = (...args: [fundIds: string[]]) => [BULK_GET_FUND_KEY_PREFIX, ...args] as const;

export const PROXY_OPTIONS_KEY_PREFIX = 'proxyOptions';
export type ProxyOptionsKey = ReturnType<typeof createProxyOptionsKey>;
export const createProxyOptionsKey = (
  ...args: [fundId: string, proxyType: ProxyTypeEnum, numLags: number, firstLayerProxyId?: string]
) => [PROXY_OPTIONS_KEY_PREFIX, ...args] as const;

export const PROXY_METADATA_KEY_PREFIX = 'proxyMetadata';
export type ProxyMetadataKey = ReturnType<typeof createProxyMetadataKey>;
export const createProxyMetadataKey = (...args: [subjects: ApiSubject[]]) =>
  [PROXY_METADATA_KEY_PREFIX, ...args] as const;

export const INVESTMENT_PROXY_METADATA_KEY_PREFIX = 'investmentProxyMetadata';
export type InvestmentProxyMetadataKey = ReturnType<typeof createInvestmentProxyMetadataKey>;
export const createInvestmentProxyMetadataKey = (investmentId: string, proxySetId: string | undefined) =>
  [INVESTMENT_PROXY_METADATA_KEY_PREFIX, investmentId, proxySetId] as const;

export const PROXY_FEASIBILITY_KEY_PREFIX = 'proxyFeasibility';
export type ProxyFeasibilityKey = ReturnType<typeof createProxyFeasibilityKey>;
export const createProxyFeasibilityKey = (
  ...args: [
    fundId: string,
    proxyId: string | undefined,
    proxyType: ProxyTypeEnum | undefined,
    numLags: number | undefined,
    extrapolate: boolean | undefined,
  ]
) => [PROXY_FEASIBILITY_KEY_PREFIX, ...args] as const;

export const BULK_PROXY_FEASIBILITY_KEY_PREFIX = 'bulkProxyFeasibility';
export type BulkProxyFeasibilityKey = ReturnType<typeof createBulkProxyFeasibilityKey>;
export const createBulkProxyFeasibilityKey = (
  ...args: [
    proxyId: string | undefined,
    proxyType: ProxyTypeEnum | undefined,
    extrapolate: boolean | undefined,
    ...fundIds: string[],
  ]
) => [BULK_PROXY_FEASIBILITY_KEY_PREFIX, ...args] as const;

export const RANGE_ANALYSIS_KEY_PREFIX = 'rangeAnalysis';
export type RangeAnalysisKey = ReturnType<typeof createRangeAnalysisKey>;
export const createRangeAnalysisKey = (...args: [subject: AnalysisSubject | undefined]) =>
  [RANGE_ANALYSIS_KEY_PREFIX, ...args] as const;

export const INVESTMENT_RANGE_ANALYSIS_KEY_PREFIX = 'investmentRangeAnalysis';
export type InvestmentRangeAnalysisKey = ReturnType<typeof createInvestmentRangeAnalysisKey>;
export const createInvestmentRangeAnalysisKey = (investmentId: string, analysisViewId: string | undefined) =>
  [INVESTMENT_RANGE_ANALYSIS_KEY_PREFIX, investmentId, analysisViewId] as const;

export const RANGE_DEBUG_KEY_PREFIX = 'rangeDebug';
export type RangeDebugKey = ReturnType<typeof createRangeDebugKey>;
export const createRangeDebugKey = (...args: [subject: AnalysisSubject | undefined, analysisViewId?: string]) =>
  [RANGE_DEBUG_KEY_PREFIX, ...args] as const;

export const AUTOPROXY_KEY_PREFIX = 'autoproxy';
export type AutoproxyKey = ReturnType<typeof createAutoproxyKey>;
export const createAutoproxyKey = (...args: [id: string, extrapolate: boolean]) =>
  [AUTOPROXY_KEY_PREFIX, ...args] as const;

export const PROXY_METRICS_KEY_PREFIX = 'proxyMetrics';
export type ProxyMetricsKey = ReturnType<typeof createProxyMetricsKey>;
export const createProxyMetricsKey = (...args: [fundId: string]) => [PROXY_METRICS_KEY_PREFIX, ...args] as const;

export const HISTORICAL_PORTFOLIO_SNAPSHOT_KEY_PREFIX = 'historicalPortfolioSnapshot';
export type HistoricalPortfolioSnapshotKey = ReturnType<typeof createHistoricalPortfolioSnapshotKey>;
export const createHistoricalPortfolioSnapshotKey = (
  ...args: [subject: AnalysisSubject | undefined, allocationsAsOfDate: number | undefined]
) => [HISTORICAL_PORTFOLIO_SNAPSHOT_KEY_PREFIX, ...args] as const;

export const TOP_HOLDINGS_KEY_PREFIX = 'topHoldings';
export type TopHoldingsKey = ReturnType<typeof createTopHoldingsKey>;
export const createTopHoldingsKey = (subjects: HoldingsSubject[], proxySetId?: string) =>
  [TOP_HOLDINGS_KEY_PREFIX, subjects, proxySetId] as const;

export const FPI_KEY_PREFIX = 'financialPerformanceIndicators';
export type fpiKey = ReturnType<typeof createFpiKey>;
export const createFpiKey = (subjects: HoldingsSubject[], proxySetId?: string) =>
  [FPI_KEY_PREFIX, subjects, proxySetId] as const;

export const PORTFOLIOS_LIBRARY_KEY_PREFIX = 'portfoliosLibrary';
export type portfoliosLibraryKey = ReturnType<typeof createFpiKey>;
export const createPortfoliosLibraryKey = (
  page: number,
  search: string,
  households: string[] | undefined,
  clientNames: string[] | undefined,
  programs: string[] | undefined,
  portfolioAccountStatuses: string[] | undefined,
  taxStatuses: TaxStatusEnum[] | undefined,
  sortField: string | undefined,
  sortOrder: string | undefined,
) =>
  [
    PORTFOLIOS_LIBRARY_KEY_PREFIX,
    page,
    search,
    households,
    clientNames,
    programs,
    portfolioAccountStatuses,
    taxStatuses,
    sortField,
    sortOrder,
  ] as const;

export const createPortfoliosLibraryFilterKey = (filterType: string) => [filterType];

export const CUSTOM_FUND_METADATA_KEY_PREFIX = 'customFundMetadata';
export type CustomFundMetadataKey = ReturnType<typeof createCustomFundMetadataKey>;
export const createCustomFundMetadataKey = (fundIds: string[]) => [CUSTOM_FUND_METADATA_KEY_PREFIX, fundIds] as const;

export const HOLDINGS_BREAKDOWN_KEY_PREFIX = 'holdingsBreakdown';
export type HoldingsCachedKey = ReturnType<typeof createHoldingsBreakdownCachedKey>;
export const createHoldingsBreakdownCachedKey = (blockType: string, subjects: HoldingsSubject[], proxySetId?: string) =>
  [HOLDINGS_BREAKDOWN_KEY_PREFIX, blockType, subjects, proxySetId] as const;

export const HOLDINGS_CATEGORIES_KEY_PREFIX = 'holdingsCategories';
export type HoldingsCategoriesKey = ReturnType<typeof createFixedStrategiesKey>;
export const createFixedStrategiesKey = (breakdownType: string, scope?: string) =>
  [HOLDINGS_CATEGORIES_KEY_PREFIX, breakdownType, scope] as const;

export const COMPOSITE_BENCHMARK_KEY_PREFIX = 'compositeBenchmark';
export type CompositeBenchmarkKey = ReturnType<typeof createCompositeBenchmarkKey>;
export const createCompositeBenchmarkKey = (fundId: string) => [COMPOSITE_BENCHMARK_KEY_PREFIX, fundId] as const;
